import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { DeleteOutline } from '@mui/icons-material';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { FormikDatePicker } from '@/common/presentation/components/forms/fields/FormikDatePicker';
import tid from '@/common/application/utils/tid';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import { FormikTimePicker } from '@/common/presentation/components/forms/fields/FormikTimePicker';
import { ERPDailyDispatchTimes } from '@/raptorjob/presentation/components/RaptorJobCreatePage/components/ERPDailyDispatchTimes';
import { useFormikContext } from 'formik';
import { addHours, differenceInHours, isValid } from 'date-fns';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';
import { useState } from 'react';
import { useOvernightJob } from '@/raptorjob/presentation/components/RaptorJobCreatePage/hook/useOvernightJob';
import { useCreateJobOptionalFieldsPreferences } from '@/raptorjob/presentation/hooks/useCreateJobOptionalFieldsPreferences';

interface ERPDispatchTimesSectionProps {
  mode?: JobFormMode;
  jobEndDateActive: boolean;
  setJobEndDateActive: (active: boolean) => void;
  setFieldVisible?: (field: 'showJobEndDate' | 'showJobEndTime', visible: boolean) => void;
}

export function ERPDispatchTimesSection({
  mode = JOB_FORM_MODES.CREATE,
  jobEndDateActive,
  setJobEndDateActive,
  setFieldVisible
}: ERPDispatchTimesSectionProps) {
  const { values, setFieldValue, setFieldError } = useFormikContext<ERPJobFormValues>();
  const isOvernightJob = useOvernightJob();
  const { visibility: optionalFieldsVisibility } = useCreateJobOptionalFieldsPreferences();

  // Track jobEndTimeActive in local state (not Formik) since it's UI state
  // Use same pattern as showVendorField: check preferences for create mode, form values for edit mode
  const [jobEndTimeActive, setJobEndTimeActive] = useState(() => {
    if (mode === 'create') {
      return optionalFieldsVisibility.showJobEndTime || Boolean(values.endTime);
    }
    return Boolean(values.endTime);
  });

  const handleClearedTimeChange = (clearedTime: Date | null) => {
    const clearedTimeValue = clearedTime && isValid(clearedTime) ? clearedTime : null;

    setFieldValue('estimatedClearedTime', clearedTimeValue, true);

    if (clearedTimeValue && values.startTime && isValid(values.startTime)) {
      setFieldValue('estimatedDuration', differenceInHours(clearedTimeValue, values.startTime), true);
    }
  };

  const handleEstimatedDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const estimatedDuration = event.target.value === '' ? null : Number(event.target.value);
    setFieldValue('estimatedDuration', estimatedDuration, true);

    if (estimatedDuration !== null && estimatedDuration > 0 && values.startTime && isValid(values.startTime)) {
      setFieldValue('estimatedClearedTime', addHours(values.startTime, estimatedDuration), true);
    } else {
      setFieldValue('estimatedClearedTime', null, true);
    }
  };

  const handleStartTimeChange = (startTime: Date | null) => {
    const startTimeValue = startTime && isValid(startTime) ? startTime : null;
    setFieldValue('startTime', startTimeValue, false);

    // If estimatedDuration exists, recalculate estimatedClearedTime
    if (startTimeValue && values.estimatedDuration !== null && values.estimatedDuration !== undefined && values.estimatedDuration > 0) {
      const calculatedClearedTime = addHours(startTimeValue, values.estimatedDuration);
      setFieldValue('estimatedClearedTime', calculatedClearedTime, true);
    }
  };

  const handleJobEndDateAdd = () => {
    setJobEndDateActive(true);
    if (mode === 'create' && setFieldVisible) {
      setFieldVisible('showJobEndDate', true);
    }
  };

  const clearEndDateField = () => {
    setFieldValue('endDate', null, false);
    setFieldError('endDate', undefined);
    setFieldValue('overnightJob', false, false);
  };

  const handleJobEndDateRemove = () => {
    setJobEndDateActive(false);
    if (mode === 'create' && setFieldVisible) {
      setFieldVisible('showJobEndDate', false);
    }
    clearEndDateField();
  };

  const handleJobEndTimeAdd = () => {
    setJobEndTimeActive(true);
    if (mode === 'create' && setFieldVisible) {
      setFieldVisible('showJobEndTime', true);
    }
  };

  const handleJobEndTimeRemove = () => {
    setJobEndTimeActive(false);
    if (mode === 'create' && setFieldVisible) {
      setFieldVisible('showJobEndTime', false);
    }
    setFieldValue('endTime', null, false);
    setFieldError('endTime', undefined);
  };

  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: '12px', fontWeight: 600 }}>
        Date
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: { xs: '0px', sm: '12px' },
          rowGap: { xs: '0px', sm: '12px' }
        }}
      >
        <FormikDatePicker<ERPJobFormValues> id="startDate" required sx={{ width: '100%' }} testId="jobs:create:start-date-input" />
        <FormikTimePicker<ERPJobFormValues>
          id="startTime"
          label="Job Start Time"
          sx={{ flex: 1 }}
          onChange={handleStartTimeChange}
          {...tid('jobs:create:start-time-input')}
        />
        <FormikTextField<ERPJobFormValues>
          id="estimatedDuration"
          label="Est. Hours"
          type="number"
          sx={{ flex: 1 }}
          inputProps={{ step: 0.5, min: 0, inputMode: 'decimal' }}
          onChange={handleEstimatedDurationChange}
          {...tid('jobs:create:estimated-hours-input')}
        />
        <FormikTimePicker<ERPJobFormValues>
          id="estimatedClearedTime"
          label="Est. Cleared Time"
          sx={{ flex: 1 }}
          onChange={handleClearedTimeChange}
          {...tid('jobs:create:estimated-cleared-time-input')}
        />
        {jobEndDateActive && (
          <Box sx={{ display: 'flex', alignItems: 'center', gridColumn: { xs: '1 / -1', sm: '1' } }}>
            <FormikDatePicker<ERPJobFormValues>
              id="endDate"
              label="Job End Date"
              required
              sx={{ flex: 1 }}
              testId="jobs:create:end-date-input"
            />
            <IconButton title="Remove Job End Date" onClick={handleJobEndDateRemove} sx={{ mb: '20px' }}>
              <DeleteOutline color="error" />
            </IconButton>
          </Box>
        )}
        {jobEndTimeActive && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gridColumn: { xs: '1 / -1', sm: jobEndDateActive ? '2' : '1' }
            }}
          >
            <FormikTimePicker<ERPJobFormValues> id="endTime" label="Job End Time" sx={{ flex: 1 }} {...tid('jobs:create:end-time-input')} />
            <IconButton title="Remove Job End Time" onClick={handleJobEndTimeRemove} sx={{ mb: '20px' }}>
              <DeleteOutline color="error" />
            </IconButton>
          </Box>
        )}
      </Box>
      {isOvernightJob && jobEndDateActive && (
        <Box>
          <Alert severity="warning" {...tid('jobs:create:overnight-job-alert')}>
            Overnight Job
          </Alert>
        </Box>
      )}
      {(!jobEndDateActive || !jobEndTimeActive) && (
        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
          {!jobEndDateActive && (
            <Button
              type="button"
              variant="text"
              onClick={handleJobEndDateAdd}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ whiteSpace: 'nowrap' }}
              {...tid('jobs:create:add-job-end-date-button')}
            >
              ADD JOB END DATE
            </Button>
          )}
          {!jobEndTimeActive && (
            <Button
              type="button"
              variant="text"
              onClick={handleJobEndTimeAdd}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ whiteSpace: 'nowrap' }}
              {...tid('jobs:create:add-job-end-time-button')}
            >
              ADD JOB END TIME
            </Button>
          )}
        </Box>
      )}
      <Box>
        <ERPDailyDispatchTimes mode={mode} />
      </Box>
    </>
  );
}
